import React from 'react'
import clsx from 'clsx'

import SelectedRectangle from '../../../components/atoms/SelectedItemRectangle'
import OverflowText from '../../../components/atoms/OverflowText'
import AccordionV2 from '../../../components/molecules/AccordionV2'
import NewCount from '../../../components/molecules/BaseConversation/NewCount'

import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { selectConvo, setInbox, setView } from '../../../slices/convo'
import { selectViews } from '../../../slices/views'
import useAccordionStyles from '../../../components/molecules/AccordionV2/styles'
import useStyles from './styles'

const countUnreads = (cache: UnreadCache, filter: string, inbox: string) => {
  if (!(filter in cache)) {
    return 0
  }

  if (!(inbox in cache[filter])) {
    return 0
  }

  let count = 0
  Object.values(cache[filter][inbox]).forEach(c => {
    count += c
  })
  return count
}

interface ClientChatInboxProps {
  inbox: APIBaseInbox
  onClick?: () => void
  className?: string
  expandView?: boolean
}

const ClientChatInbox: React.FC<ClientChatInboxProps> = ({
  inbox,
  onClick,
  className,
  expandView
}) => {
  const dispatch = useAppDispatch()
  const {
    inbox: selectedInbox,
    view: selectedView,
    topFilter,
    unreadCache
  } = useAppSelector(selectConvo)
  const { views } = useAppSelector(selectViews)

  const inboxViews = React.useMemo(
    () =>
      views.filter(
        view =>
          view.type === 'inbox' && view.item_id === inbox.id && !view.hidden
      ),
    [views, inbox.id]
  )

  const accordionClasses = useAccordionStyles()
  const classes = useStyles()

  const handleClick = React.useCallback(() => {
    dispatch(setInbox(inbox.id))
    dispatch(setView(null))
    onClick && onClick()
  }, [dispatch, inbox, onClick])

  const handleViewClick = React.useCallback(
    (id: string) => {
      handleClick()
      dispatch(setView(id))
    },
    [handleClick, dispatch]
  )

  const title = inbox.name
  const selected = selectedInbox === inbox.id
  const count = countUnreads(unreadCache, topFilter, inbox.id)

  if (inboxViews.length === 0) {
    return (
      <div className={className}>
        <div
          onClick={handleClick}
          className={clsx(classes.summaryContainer, 'no-views')}
        >
          <div
            className={clsx(
              accordionClasses.header,
              selected && accordionClasses.selected,
              classes.header
            )}
          >
            {selected && <SelectedRectangle />}

            <OverflowText>
              <label>{title}</label>
            </OverflowText>

            {count > 0 && (
              <NewCount count={count} dontGroup className={classes.count} />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <AccordionV2
      title={title}
      selected={selected && !selectedView}
      forceExpand={
        expandView && inboxViews.map(view => view.id).includes(selectedView)
      }
      onClick={handleClick}
      count={count}
      className={className}
    >
      <section className={classes.root}>
        {inboxViews.map(view => {
          const count = countUnreads(unreadCache, topFilter, view.id)

          return (
            <div
              key={view.id}
              onClick={() => handleViewClick(view.id)}
              className={clsx(
                classes.viewOption,
                selectedView === view.id && classes.selected
              )}
            >
              {selectedView === view.id && <SelectedRectangle />}
              <OverflowText>
                <label>{view.name}</label>
              </OverflowText>

              {count > 0 && (
                <NewCount count={count} dontGroup className={classes.count} />
              )}
            </div>
          )
        })}
      </section>
    </AccordionV2>
  )
}

export default ClientChatInbox
